import { FC } from 'react';
import { GRUPPO_CONTESTO_GIOCO, GRUPPO_MALLO } from '@leopart/calc';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomTextInput from '../../../shared/CustomInput/CustomTextInput';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import Typhography from '../../../shared/Typhography/Typhography';
import { ITrainingCategory } from '../../../models/allenamenti';
import { ICategoryFormDraft } from './useCategoriesModal';
import { CATEGORY_SWATCHES } from './categoriesModal.constants';
import styles from './CategoryForm.module.scss';

interface ICategoryFormProps {
  draft: ICategoryFormDraft;
  /** Le sottocategorie ereditano il colore della macro: niente tavolozza. */
  showColor: boolean;
  isValid: boolean;
  isSaving: boolean;
  submitLabel: string;
  error?: string | null;
  /**
   * Solo in modifica: tutte le macro, per spostare la voce sotto un'altra.
   * Assente in creazione, dove il posto lo dice già il «+» da cui si è partiti.
   */
  macros?: ITrainingCategory[];
  /** Lo slug in modifica: si esclude da sé stesso nella tendina. */
  currentSlug?: string;
  currentParent?: string | null;
  onMoveTo?: (parentSlug: string | null) => void;
  onChange: <K extends keyof ICategoryFormDraft>(
    key: K,
    value: ICategoryFormDraft[K],
  ) => void;
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * Creazione e modifica di una categoria.
 *
 * NON è una modal: è il contenuto che prende il posto dell'elenco dentro la
 * stessa modal. Averlo fatto come secondo dialogo sopra il primo dava due
 * riquadri bianchi impilati senza stacco fra loro — illeggibile, perché nulla
 * diceva quale dei due fosse attivo.
 *
 * Il titolo (che imposta chi lo ospita) dice dove finirà la voce, "Nuova voce
 * in «Forza»": è l'informazione che prima dava la posizione del form.
 */
/** I due gruppi di campi extra attivabili su una macro (ADR 029). */
const GRUPPI_CAMPI = [
  { slug: GRUPPO_CONTESTO_GIOCO, label: 'Contesto di gioco (campo, giocatori, tempo effettivo)' },
  { slug: GRUPPO_MALLO, label: 'Classificazione Mallo (peso % gara + flusso)' },
];

const CategoryForm: FC<ICategoryFormProps> = ({
  draft,
  showColor,
  isValid,
  isSaving,
  submitLabel,
  error,
  macros,
  currentSlug,
  currentParent,
  onMoveTo,
  onChange,
  onSubmit,
  onClose,
}) => (
    <div className={styles.body}>
      <CustomTextInput
        label="Nome"
        placeholder="Es. Prevenzione infortuni"
        value={draft.labelIt}
        onChange={(e) => onChange('labelIt', e.target.value)}
      />

      {/*
        Lo spostamento è un'azione a sé, non un campo del form: cambia la
        gerarchia e va a buon fine o fallisce per conto suo (il server rifiuta
        di annidare una macro che ha già delle sotto). Applicarlo insieme alla
        rinomina renderebbe ambiguo cosa è stato salvato quando una delle due
        fallisce.
      */}
      {macros && onMoveTo && (
        <CustomSelectInput
          label="Dentro"
          placeholder="— categoria principale —"
          options={[
            { label: '— categoria principale —', value: '' },
            ...macros
              .filter((m) => m.slug !== currentSlug)
              .map((m) => ({ label: m.labelIt, value: m.slug })),
          ]}
          value={currentParent ?? ''}
          onChange={(value) => onMoveTo((value as string) || null)}
          /* Il menu vive in un portal sul body: dentro il corpo della modal,
             che ha overflow, verrebbe ritagliato e sembrerebbe non aprirsi. */
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
        />
      )}

      {showColor && (
        <div className={styles.colorBlock}>
          <Typhography size="medium-12" className={styles.colorLabel}>
            Colore
          </Typhography>
          <div className={styles.swatches}>
            {CATEGORY_SWATCHES.map((hex) => {
              const isOn = draft.color.toLowerCase() === hex.toLowerCase();
              return (
                <button
                  key={hex}
                  type="button"
                  aria-label={`Colore ${hex}`}
                  aria-pressed={isOn}
                  className={isOn ? `${styles.swatch} ${styles.swatchOn}` : styles.swatch}
                  /* Il colore È il dato: non esiste una classe per un valore
                     libero, quindi qui l'inline style è contenuto, non stile. */
                  style={{ background: hex }}
                  onClick={() => onChange('color', hex)}
                />
              );
            })}
            {/* Ruota di colori e non il colore corrente: mostrando quello
                scelto sembrava un undicesimo campione della tavolozza. */}
            <label className={styles.custom} title="Colore personalizzato">
              <input
                type="color"
                value={draft.color}
                aria-label="Colore personalizzato"
                onChange={(e) => onChange('color', e.target.value)}
              />
              <span />
            </label>
          </div>
        </div>
      )}

      {/* I gruppi campi valgono solo per le macro (le sotto li ignorano):
          stessa condizione della tavolozza colore. */}
      {showColor && (
        <div className={styles.groupsBlock}>
          <Typhography size="medium-12" className={styles.colorLabel}>
            Campi extra sul blocco
          </Typhography>
          {GRUPPI_CAMPI.map((g) => (
            <label key={g.slug} className={styles.groupToggle}>
              <input
                type="checkbox"
                checked={draft.fieldsEnabled.includes(g.slug)}
                onChange={(e) =>
                  onChange(
                    'fieldsEnabled',
                    e.target.checked
                      ? [...draft.fieldsEnabled, g.slug]
                      : draft.fieldsEnabled.filter((s) => s !== g.slug),
                  )
                }
              />
              <span>{g.label}</span>
            </label>
          ))}
        </div>
      )}

      {error && (
        <div className={styles.error} role="alert">
          <Typhography size="medium-12" className={styles.errorText}>
            {error}
          </Typhography>
        </div>
      )}

      <div className={styles.footer}>
        <CustomButton size="sm" color="outline" onClick={onClose}>
          Annulla
        </CustomButton>
        <CustomButton
          size="sm"
          color="secondary"
          disabled={!isValid}
          isLoading={isSaving}
          onClick={onSubmit}
        >
          {submitLabel}
        </CustomButton>
      </div>
    </div>
);

export default CategoryForm;
